// Shared state of the "talk to the virtual assistant" action (ScanLanding, StaffNotAvailable):
// one request at a time, one generic message for any failure, and the assistant shown as
// unavailable (no credit left, service down) when the backend says so before the guest taps.
import { useEffect, useRef, useState } from 'react'
import { checkAssistant, openAssistant } from '../assistant'
import type { ScanTokens } from '../assistant'
import { currentLanguage } from '../i18n'

export const useAssistantLauncher = (scan: ScanTokens | null, offered = true) => {
  const [opening, setOpening] = useState(false)
  const [failed, setFailed] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const inFlight = useRef(false)

  // ONLY WHEN THE PAGE OFFERS THE ASSISTANT; WHILE THE ANSWER IS PENDING THE BUTTON STAYS USABLE.
  const token = scan?.token
  const details = scan?.details
  useEffect(() => {
    if (!offered || !token || !details) return
    let current = true
    void checkAssistant(token, details).then((available) => {
      if (current) setUnavailable(!available)
    })
    return () => {
      current = false
    }
  }, [offered, token, details])

  // BACK FROM THE ASSISTANT THROUGH THE BROWSER HISTORY (PAGE RESTORED FROM CACHE): THE
  // BUTTON MUST NOT STAY BUSY.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      inFlight.current = false
      setOpening(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const open = async () => {
    if (inFlight.current) return
    if (!scan) {
      setFailed(true)
      return
    }
    inFlight.current = true
    setOpening(true)
    setFailed(false)
    try {
      await openAssistant(scan.token, scan.details, currentLanguage)
      // THE BROWSER IS LEAVING FOR THE ASSISTANT: STAY BUSY UNTIL IT DOES.
    } catch {
      inFlight.current = false
      setOpening(false)
      setFailed(true)
    }
  }

  return { open, opening, failed, unavailable }
}
